// src/components/settings/AccountTab.tsx
import React, { useState } from 'react';
import { LogOut, Shield, MessageCircle, Dumbbell, Utensils, Zap, Brain, UserX, X, AlertTriangle, Loader2 } from 'lucide-react';
import { getSupabase, signOut } from "@/lib/supabase";

// ——————————————————————————————————
// TYPES
// ——————————————————————————————————
interface ActionItem {
  label: string;
  action: string;
  icon: React.ElementType;
  phrase: string;
  description: string;
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  action: ActionItem | null;
  isLoading: boolean;
  step: 'confirm' | 'phrase' | 'email';
  inputValue: string;
  onInputChange: (value: string) => void;
  userEmail?: string;
}

// ——————————————————————————————————
// CONFIRMATION MODAL
// ——————————————————————————————————
function ConfirmModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  action, 
  isLoading,
  step,
  inputValue,
  onInputChange,
  userEmail
}: ConfirmModalProps) {
  if (!isOpen || !action) return null;

  const isDeleteAccount = action.action === 'delete_full_account';

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#f8fafc] border border-red-500/50 rounded-2xl p-4 md:p-6 w-full max-w-md animate-in zoom-in-95">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${
              isDeleteAccount ? 'bg-red-500/20' : 'bg-red-500/10'
            }`}>
              <AlertTriangle className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
            </div>
            <div>
              <h3 className="font-bold text-[#0f172a] text-base md:text-lg">{action.label}</h3>
              <p className="text-xs md:text-sm text-gray-500">This cannot be undone</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            disabled={isLoading}
            className="p-1.5 hover:bg-black/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content based on step */}
        <div className="space-y-4">
          {step === 'confirm' && (
            <>
              <p className="text-sm md:text-base text-gray-600">
                Are you sure you want to <span className="text-red-400 font-medium">{action.label.toLowerCase()}</span>?
              </p>
              <p className="text-xs md:text-sm text-gray-500">
                {action.description}
              </p>
            </>
          )}

          {step === 'phrase' && (
            <>
              <p className="text-sm md:text-base text-gray-600">
                To confirm, type the following phrase exactly:
              </p>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <code className="text-red-400 text-sm md:text-base font-mono break-all">{action.phrase}</code>
              </div>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder="Type the phrase above..."
                className="w-full bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2.5 text-sm md:text-base text-[#0f172a] placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors"
                autoFocus
                disabled={isLoading}
              />
            </>
          )}

          {step === 'email' && (
            <>
              <div className="bg-red-600/20 border border-red-600 rounded-lg p-3 md:p-4">
                <p className="text-sm md:text-base text-red-300 font-bold">
                  ⚠️ FINAL WARNING
                </p>
                <p className="text-xs md:text-sm text-red-400 mt-1">
                  This will permanently delete your entire account and all associated data. This action is irreversible.
                </p>
              </div>
              <p className="text-sm md:text-base text-gray-600">
                Type your email to confirm:
              </p>
              <input
                type="email"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder={userEmail || "your@email.com"}
                className="w-full bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2.5 text-sm md:text-base text-[#0f172a] placeholder-gray-500 focus:border-red-500 focus:outline-none transition-colors"
                autoFocus
                disabled={isLoading}
              />
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2.5 md:py-3 bg-gray-700 hover:bg-gray-600 text-[#0f172a] text-sm md:text-base font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading || (step === 'phrase' && inputValue !== action.phrase) || (step === 'email' && inputValue !== userEmail)}
            className={`flex-1 px-4 py-2.5 md:py-3 font-bold text-sm md:text-base rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              isDeleteAccount 
                ? 'bg-red-600 hover:bg-red-700 text-[#0f172a]' 
                : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Processing...</span>
              </>
            ) : step === 'confirm' ? (
              'Continue'
            ) : step === 'phrase' ? (
              isDeleteAccount ? 'Continue' : 'Confirm'
            ) : (
              'Delete Forever'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ——————————————————————————————————
// TOAST COMPONENT
// ——————————————————————————————————
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 left-4 md:left-auto md:w-96 px-4 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-in slide-in-from-bottom ${
      type === 'success' ? 'bg-green-500/90' : 'bg-red-500/90'
    } backdrop-blur-sm text-[#0f172a]`}>
      <p className="text-sm flex-1">{message}</p>
      <button onClick={onClose} className="p-1 hover:bg-black/20 rounded">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// ——————————————————————————————————
// MAIN ACCOUNT TAB
// ——————————————————————————————————
export default function AccountTab({ onLogout }: { onLogout: () => void }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ActionItem | null>(null);
  const [step, setStep] = useState<'confirm' | 'phrase' | 'email'>('confirm');
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const actions: ActionItem[] = [
    { 
      label: "Delete Conversations", 
      action: "delete_conversations", 
      icon: MessageCircle, 
      phrase: "DELETE CONVERSATIONS",
      description: "This will permanently delete all your chat history with the AI coach."
    },
    { 
      label: "Delete Training Logs", 
      action: "delete_training_logs", 
      icon: Dumbbell, 
      phrase: "DELETE TRAINING LOGS",
      description: "This will permanently delete all your workout and training data."
    },
    { 
      label: "Delete Nutrition Logs", 
      action: "delete_nutrition_logs", 
      icon: Utensils, 
      phrase: "DELETE NUTRITION LOGS",
      description: "This will permanently delete all your meal plans and nutrition tracking."
    },
    { 
      label: "Disconnect Wearables", 
      action: "disconnect_wearables", 
      icon: Zap, 
      phrase: "DISCONNECT WEARABLES",
      description: "This will disconnect all connected devices (Garmin, WHOOP, etc)."
    },
    { 
      label: "Reset AI Memory", 
      action: "reset_ai_memory", 
      icon: Brain, 
      phrase: "RESET AI MEMORY",
      description: "This will clear the AI's memory of your preferences and history."
    },
    { 
      label: "Delete Full Account", 
      action: "delete_full_account", 
      icon: UserX, 
      phrase: "DELETE MY ACCOUNT PERMANENTLY",
      description: "This will permanently delete your entire account and all associated data."
    },
  ];

  const openModal = async (action: ActionItem) => {
    setSelectedAction(action);
    setStep('confirm');
    setInputValue('');
    setModalOpen(true);

    // Pre-fetch user email for account deletion
    if (action.action === 'delete_full_account') {
      const { data: { user } } = await getSupabase().auth.getUser();
      setUserEmail(user?.email || '');
    }
  };

  const handleConfirm = async () => {
    if (!selectedAction) return;

    // Step progression
    if (step === 'confirm') {
      setStep('phrase');
      setInputValue('');
      return;
    }

    if (step === 'phrase') {
      if (inputValue !== selectedAction.phrase) return;
      
      if (selectedAction.action === 'delete_full_account') {
        setStep('email');
        setInputValue('');
        return;
      }
    }

    if (step === 'email') {
      if (inputValue !== userEmail) return;
    }

    // Execute the action
    setIsLoading(true);
    
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("Not logged in");

      // Get phone number
      const { data: clientData } = await supabase
        .from("clients")
        .select("phone")
        .eq("user_id", session.user.id)
        .single();

      const phone = clientData?.phone || null;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-data`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            action: selectedAction.action,
            phone
          }),
        }
      );

      if (res.ok) {
        setModalOpen(false);
        
        if (selectedAction.action === "delete_full_account") {
          setToast({ message: "Your account has been permanently deleted.", type: 'success' });
          setTimeout(async () => {
            await signOut();
            window.location.href = "/login";
          }, 1500);
        } else {
          setToast({ message: `${selectedAction.label} completed successfully.`, type: 'success' });
        }
      } else {
        const text = await res.text();
        throw new Error(text || "Request failed");
      }
    } catch (err: any) {
      console.error("[AccountTab] Action failed:", err);
      setToast({ message: err.message || "Request failed. Please try again.", type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    if (!isLoading) {
      setModalOpen(false);
      setSelectedAction(null);
      setStep('confirm');
      setInputValue('');
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Toast */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={modalOpen}
        onClose={closeModal}
        onConfirm={handleConfirm}
        action={selectedAction}
        isLoading={isLoading}
        step={step}
        inputValue={inputValue}
        onInputChange={setInputValue}
        userEmail={userEmail}
      />

      {/* Logout Section */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <h2 className="text-base md:text-lg font-semibold mb-3 md:mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 md:w-5 md:h-5" /> Account
        </h2>
        <button
          onClick={onLogout}
          className="w-full flex items-center justify-between py-2.5 md:py-3 px-3 md:px-4 bg-[#f8fafc] rounded-lg hover:bg-[#f1f5f9] transition-colors"
        >
          <span className="flex items-center gap-2 md:gap-3">
            <LogOut className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
            <span className="text-sm md:text-base">Logout</span>
          </span>
        </button>
      </div>

      {/* Danger Zone */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 md:p-6">
        <h2 className="text-xl md:text-2xl font-bold text-red-400 mb-4 md:mb-6">Danger Zone</h2>
        
        <div className="space-y-3 md:space-y-5">
          {actions.map(({ label, action, icon: Icon, phrase, description }) => {
            const isDeleteAccount = action === "delete_full_account";
            
            return (
              <div
                key={action}
                className={`p-3 md:p-6 rounded-xl border transition-all ${
                  isDeleteAccount
                    ? "bg-red-900/30 border-red-700"
                    : "bg-red-900/10 border-red-800/50 hover:bg-red-900/20"
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-black/5 flex items-center justify-center flex-shrink-0">
                      <Icon className={`w-5 h-5 md:w-7 md:h-7 ${isDeleteAccount ? "text-[#0f172a]" : "text-red-400"}`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm md:text-lg text-[#0f172a]">
                        {label}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-500 mt-0.5 line-clamp-1">
                        {description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => openModal({ label, action, icon: Icon, phrase, description })}
                    className={`w-full sm:w-auto px-4 md:px-8 py-2.5 md:py-4 rounded-xl text-sm md:text-base font-bold transition-all active:scale-95 flex-shrink-0 ${
                      isDeleteAccount
                        ? "bg-red-600 hover:bg-red-700 text-[#0f172a] shadow-lg"
                        : "bg-black/10 hover:bg-black/20 text-[#0f172a] border border-black/20"
                    }`}
                  >
                    {isDeleteAccount ? "Delete Account" : "Execute"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Warning Footer */}
        <div className="mt-6 md:mt-10 p-4 md:p-8 bg-black/40 rounded-xl md:rounded-2xl border-2 border-red-600 text-center">
          <p className="text-red-300 font-bold text-sm md:text-lg">
            These actions are irreversible.
          </p>
          <p className="text-red-400 mt-1 md:mt-2 text-xs md:text-base">
            You will be asked to type a confirmation phrase for each one.
          </p>
        </div>
      </div>
    </div>
  );
}
